import { Box, Button, Typography } from "@mui/material";
import { observer } from "mobx-react";
import { FC } from "react";

import { useChatStore } from "../../../hooks/useChatStore";
import { useAppStore } from "../../../hooks/useAppStore";
import EyeIcon from "../../icons/EyeIcon";
import ChevronLeftIcon from "../../icons/ChevronLeftIcon";
import Row from "../../ui/core/Row";

/**
 * Fixed contract summary bar shown above the chat input for contract-research
 * chats (when the contract preview panel is closed). "Open Document" reopens
 * the same document in the side panel.
 */
const ContractSummaryBar: FC = observer(() => {
  const chatStore = useChatStore();
  const { contractToPreview, setContractToPreview } = useAppStore();

  const documentIdFromChat =
    chatStore.contractPreviewForChat?.document_id ?? null;
  const documentTitleFromChat =
    chatStore.contractPreviewForChat?.title ?? null;

  // contractToPreview is global and outlives the chat that set it, so only
  // fall back to it when it refers to the same document as this chat's.
  const isSameDocument = documentIdFromChat
    ? contractToPreview?.document_id === documentIdFromChat
    : true;

  const documentId =
    documentIdFromChat ??
    (isSameDocument ? contractToPreview?.document_id : undefined);
  const documentTitle =
    documentTitleFromChat ??
    (isSameDocument ? contractToPreview?.title : undefined);

  const handleOpenDocument = () => {
    if (!documentId) return;

    chatStore.setFileToPreview(null);
    chatStore.setLetterToPreview(null);
    setContractToPreview({
      document_id: documentId,
      title: documentTitle || "",
    });
  };

  // The contract preview side panel already shows this document when open
  const isThisDocumentOpenInPanel =
    !chatStore.fileToPreview &&
    !chatStore.letterToPreview &&
    !!contractToPreview &&
    isSameDocument;

  if (isThisDocumentOpenInPanel) {
    return null;
  }

  if (!documentId) {
    return null;
  }

  return (
    <Box
      sx={{
        px: 2,
        py: 1,
        width: "100%",
        borderBottom: "1px solid",
        borderColor: "divider",
        backgroundColor: "white",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Row
          sx={{
            alignItems: "center",
            gap: 1,
            flex: 1,
            minWidth: 0,
            flexWrap: "nowrap",
          }}
        >
          <ChevronLeftIcon className="w-4 h-4 shrink-0 text-gray-500" />

          <Typography fontWeight={600} noWrap sx={{ minWidth: 0 }}>
            {documentTitle || documentId}
          </Typography>
        </Row>

        <Button
          variant="contained"
          color="primary"
          size="small"
          endIcon={<EyeIcon width={16} height={16} />}
          onClick={handleOpenDocument}
          sx={{ flexShrink: 0, borderRadius: "999px", px: 2 }}
        >
          Open Document
        </Button>
      </Box>
    </Box>
  );
});

export default ContractSummaryBar;
